import { Children, type ReactNode } from "react";
import { Link } from "../lib/router";
import { SectionHead } from "./ui";

/** The line a list or a table shows when it has no rows. */
export function EmptyRow({ children }: { children: ReactNode }) {
  return <div className="px-4 py-8 text-center text-sm text-muted">{children}</div>;
}

/**
 * The one list: rows in a white card, divided by hairlines, the same on every page.
 * Rows are usually ListRow; a row of its own keeps ListRow's padding (`listRowCls`).
 */
export function List({
  title,
  hint,
  action,
  filters,
  empty,
  footer,
  scroll,
  children,
  className = "",
}: {
  title?: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  /** A search box or filters, between the title and the box. */
  filters?: ReactNode;
  /** Shown in the box when there are no rows. */
  empty?: ReactNode;
  /** A last part of the box under the rows: an add form, a note on what the list covers. */
  footer?: ReactNode;
  /** Rows scroll inside the box past a height: "short" (an inline history), "tall" (a live feed). */
  scroll?: "short" | "tall";
  children?: ReactNode;
  className?: string;
}) {
  const rows = Children.toArray(children);
  return (
    <section className={`min-w-0 ${className}`}>
      <SectionHead title={title} hint={hint} action={action} />
      {filters && <div className="mb-3">{filters}</div>}
      <div className="card divide-y divide-line overflow-hidden">
        {rows.length ? (
          scroll ? <div className={`scroll-thin divide-y divide-line overflow-auto ${scroll === "tall" ? "max-h-[70vh]" : "max-h-72"}`}>{rows}</div> : rows
        ) : (
          empty !== undefined && <EmptyRow>{empty}</EmptyRow>
        )}
        {footer && <div className="bg-fill-subtle px-4 py-3 text-sm">{footer}</div>}
      </div>
    </section>
  );
}

export const listRowCls = "px-4 py-3";
const rowHover = "transition hover:bg-fill-subtle focus-visible:bg-fill-subtle focus-visible:outline-none";

/**
 * One row of a List: what it is on the left (a picture, a title, a line under it), its status and actions on the right.
 * On a phone the right side moves under the left. `to` makes the whole row a link; `onClick` makes it clickable.
 */
export function ListRow({
  leading,
  title,
  subtitle,
  trailing,
  children,
  to,
  onClick,
  className = "",
}: {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  /** Anything under the title and subtitle, inside the row. */
  children?: ReactNode;
  to?: string;
  onClick?: () => void;
  className?: string;
}) {
  const body = (
    <>
      <div className="flex min-w-0 grow basis-48 items-center gap-3">
        {leading && <div className="shrink-0">{leading}</div>}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">{title}</div>
          {subtitle && <div className="mt-0.5 truncate text-sm text-muted">{subtitle}</div>}
          {children}
        </div>
      </div>
      {trailing && <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2 text-xs text-muted">{trailing}</div>}
    </>
  );
  const cls = `flex flex-wrap items-center gap-x-4 gap-y-2 ${listRowCls} ${className}`;
  if (to) return <Link to={to} className={`${cls} ${rowHover}`}>{body}</Link>;
  if (onClick)
    return (
      <div role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && onClick()} className={`${cls} ${rowHover}`}>
        {body}
      </div>
    );
  return <div className={cls}>{body}</div>;
}
